import { useRef, useState } from 'react';
import { ReflexContainer } from 'react-reflex';
import { useResizeWatcher } from '@/core/hooks/useResizeWatcher.hook';
import type { ReactNode } from 'react';

export type ReflexHostSize = {
  width: number;
  height: number;
};

type SizedReflexContainerProps = {
  children: ReactNode | ((hostSize: ReflexHostSize) => ReactNode);
  orientation?: 'horizontal' | 'vertical';
};

/**
 * Defers mounting react-reflex until the host element has a non-zero size.
 * ReflexContainer computes pane flex values once on mount; if height is 0 at that moment the layout stays broken.
 */
export const SizedReflexContainer = ({ children, orientation = 'horizontal' }: SizedReflexContainerProps) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const [hostSize, setHostSize] = useState<ReflexHostSize | undefined>();

  useResizeWatcher(
    {
      elementRef: hostRef,
      callback: ({ newHeight, newWidth }) => {
        const size = orientation === 'horizontal' ? newHeight : newWidth;
        if (size > 0) {
          setHostSize((currentSize) => currentSize ?? { width: newWidth, height: newHeight });
        }
      },
      callOnLoad: true,
    },
    [orientation],
  );

  const content = hostSize && (typeof children === 'function' ? children(hostSize) : children);

  return (
    <div ref={hostRef} className="flexLayoutSplitterHost">
      {content && (
        <ReflexContainer orientation={orientation} windowResizeAware>
          {content}
        </ReflexContainer>
      )}
    </div>
  );
};
